import type { Ohaeng } from '../types/elements';

/**
 * 십이신살 조견표
 *
 * 년지(또는 일지)의 삼합 그룹에 따라 12가지 신살 위치를 결정한다.
 * 삼합 그룹: 申子辰(水), 寅午戌(火), 巳酉丑(金), 亥卯未(木)
 */

/**
 * 지지 인덱스 → 삼합 그룹 인덱스 매핑
 *
 * - 申(8), 子(0), 辰(4) → 그룹 0 (水)
 * - 寅(2), 午(6), 戌(10) → 그룹 1 (火)
 * - 巳(5), 酉(9), 丑(1) → 그룹 2 (金)
 * - 亥(11), 卯(3), 未(7) → 그룹 3 (木)
 *
 * @param branchIdx - 지지 인덱스 (0~11)
 * @returns 삼합 그룹 인덱스 (0~3)
 */
export function getSamhapGroup(branchIdx: number): number {
  const groupMap: Record<number, number> = {
    8: 0, 0: 0, 4: 0,   // 申子辰
    2: 1, 6: 1, 10: 1,  // 寅午戌
    5: 2, 9: 2, 1: 2,   // 巳酉丑
    11: 3, 3: 3, 7: 3,  // 亥卯未
  };
  return groupMap[branchIdx];
}

/**
 * 십이신살 위치 (삼합 그룹별)
 *
 * 각 행: [겁살, 재살, 천살, 지살, 연살, 월살, 망신살, 장성살, 반안살, 역마살, 육해살, 화개살]
 * 값은 해당 신살이 나타나는 지지 인덱스
 */
export const TWELVE_SINSAL_TABLE: readonly (readonly number[])[] = [
  // 그룹 0 (申子辰/水):
  [5, 6, 7, 8, 9, 10, 11, 0, 1, 2, 3, 4],
  // 그룹 1 (寅午戌/火):
  [11, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  // 그룹 2 (巳酉丑/金):
  [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1],
  // 그룹 3 (亥卯未/木):
  [8, 9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7],
] as const;

/**
 * 천을귀인 (天乙貴人) — 일간 기준
 *
 * 일간 인덱스 → 천을귀인이 나타나는 지지 인덱스 배열
 */
export const CHEONUL_GUIIN: Record<number, number[]> = {
  0: [1, 7],   // 甲 → 丑未
  1: [0, 8],   // 乙 → 子申
  2: [11, 9],  // 丙 → 亥酉
  3: [11, 9],  // 丁 → 亥酉
  4: [1, 7],   // 戊 → 丑未
  5: [0, 8],   // 己 → 子申
  6: [1, 7],   // 庚 → 丑未
  7: [2, 6],   // 辛 → 寅午
  8: [3, 5],   // 壬 → 卯巳
  9: [3, 5],   // 癸 → 卯巳
};

/**
 * 일덕귀인 (日德貴人) — 일간 기준
 *
 * 일간 인덱스 → 일덕귀인이 나타나는 지지 인덱스 배열.
 * 상대의 지지(주로 일지)에 해당 지지가 있으면 성립.
 */
export const ILDEOK_GUIIN: Record<number, number[]> = {
  0: [2],      // 甲 → 寅
  1: [3],      // 乙 → 卯
  2: [5],      // 丙 → 巳
  3: [6],      // 丁 → 午
  4: [4, 10],  // 戊 → 辰戌
  5: [1, 7],   // 己 → 丑未
  6: [8],      // 庚 → 申
  7: [9],      // 辛 → 酉
  8: [10],     // 壬 → 戌
  9: [0],      // 癸 → 子
};

/**
 * 양인살 (羊刃殺) — 일간 기준
 *
 * 전간(10간) 적용 (sal04 기준). 일간 인덱스 → 지지 인덱스
 */
export const YANGIN_SAL: Record<number, number> = {
  0: 3,   // 甲 → 卯
  1: 4,   // 乙 → 辰
  2: 6,   // 丙 → 午
  3: 7,   // 丁 → 未
  4: 6,   // 戊 → 午
  5: 7,   // 己 → 未
  6: 9,   // 庚 → 酉
  7: 10,  // 辛 → 戌
  8: 0,   // 壬 → 子
  9: 1,   // 癸 → 丑
};

/**
 * 괴강살 (魁罡殺) — 일주 간지 기준
 *
 * 육십갑자 인덱스: 庚辰(16), 壬辰(28), 戊戌(34), 庚戌(46)
 */
export const KUIGANG_INDICES: readonly number[] = [16, 28, 34, 46];

/**
 * 공망 (空亡) — 일주의 육십갑자 순(旬) 기준
 *
 * 10일 주기에서 빠진 두 지지를 반환한다.
 *
 * @param sexagenaryIndex - 육십갑자 인덱스 (0~59)
 * @returns 공망에 해당하는 두 지지 인덱스
 */
export function getGongmang(sexagenaryIndex: number): [number, number] {
  const cycle = Math.floor(sexagenaryIndex / 10);
  const firstEmpty = 10 - (cycle * 2 % 12);
  const a = ((firstEmpty) + 12) % 12;
  const b = ((firstEmpty + 1)) % 12;
  return [a, b];
}

/**
 * 문창귀인 (文昌貴人) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const MUNCHANG_GUIIN: Record<number, number> = {
  0: 5,   // 甲 → 巳
  1: 6,   // 乙 → 午
  2: 8,   // 丙 → 申
  3: 9,   // 丁 → 酉
  4: 8,   // 戊 → 申
  5: 9,   // 己 → 酉
  6: 11,  // 庚 → 亥
  7: 0,   // 辛 → 子
  8: 2,   // 壬 → 寅
  9: 3,   // 癸 → 卯
};

/**
 * 홍염살 (紅艶殺) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const HONGYEOM_SAL: Record<number, number> = {
  0: 6,   // 甲 → 午
  1: 6,   // 乙 → 午
  2: 2,   // 丙 → 寅
  3: 7,   // 丁 → 未
  4: 4,   // 戊 → 辰
  5: 4,   // 己 → 辰
  6: 10,  // 庚 → 戌
  7: 9,   // 辛 → 酉
  8: 0,   // 壬 → 子
  9: 8,   // 癸 → 申
};

/**
 * 태극귀인 (太極貴人) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스 배열
 */
export const TAEGUK_GUIIN: Record<number, number[]> = {
  0: [0, 6],        // 甲 → 子午
  1: [0, 6],        // 乙 → 子午
  2: [3, 9],        // 丙 → 卯酉
  3: [3, 9],        // 丁 → 卯酉
  4: [4, 10, 1, 7], // 戊 → 辰戌丑未
  5: [4, 10, 1, 7], // 己 → 辰戌丑未
  6: [2, 11],       // 庚 → 寅亥
  7: [2, 11],       // 辛 → 寅亥
  8: [5, 8],        // 壬 → 巳申
  9: [5, 8],        // 癸 → 巳申
};

/**
 * 월덕귀인 (月德貴人) — 월지의 삼합 그룹 기준
 *
 * 삼합 그룹 → 필요한 천간 인덱스
 */
export const WOLDEOK_GUIIN: Record<number, number> = {
  0: 8,  // 申子辰月 → 壬(8)
  1: 2,  // 寅午戌月 → 丙(2)
  2: 6,  // 巳酉丑月 → 庚(6)
  3: 0,  // 亥卯未月 → 甲(0)
};

/**
 * 천덕귀인 (天德貴人) — 월지 기준
 *
 * 월지 인덱스 → `{ type: 천간/지지 구분, index: 인덱스 }`
 */
export const CHEONDEOK_GUIIN: Record<number, { type: 'stem' | 'branch'; index: number }> = {
  2: { type: 'stem', index: 3 },     // 寅月 → 丁
  3: { type: 'branch', index: 8 },   // 卯月 → 申
  4: { type: 'stem', index: 8 },     // 辰月 → 壬
  5: { type: 'stem', index: 7 },     // 巳月 → 辛
  6: { type: 'branch', index: 11 },  // 午月 → 亥
  7: { type: 'stem', index: 0 },     // 未月 → 甲
  8: { type: 'stem', index: 9 },     // 申月 → 癸
  9: { type: 'branch', index: 2 },   // 酉月 → 寅
  10: { type: 'stem', index: 2 },    // 戌月 → 丙
  11: { type: 'stem', index: 1 },    // 亥月 → 乙
  0: { type: 'branch', index: 5 },   // 子月 → 巳
  1: { type: 'stem', index: 6 },     // 丑月 → 庚
};

/**
 * 천주귀인 (天廚貴人) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const CHEONJU_GUIIN: Record<number, number> = {
  0: 5,   // 甲 → 巳
  1: 6,   // 乙 → 午
  2: 5,   // 丙 → 巳
  3: 6,   // 丁 → 午
  4: 8,   // 戊 → 申
  5: 9,   // 己 → 酉
  6: 11,  // 庚 → 亥
  7: 0,   // 辛 → 子
  8: 2,   // 壬 → 寅
  9: 3,   // 癸 → 卯
};

/**
 * 천관귀인 (天官貴人) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const CHEONGWAN_GUIIN: Record<number, number> = {
  0: 7,   // 甲 → 未
  1: 4,   // 乙 → 辰
  2: 9,   // 丙 → 酉
  3: 0,   // 丁 → 子
  4: 9,   // 戊 → 酉
  5: 0,   // 己 → 子
  6: 11,  // 庚 → 亥
  7: 2,   // 辛 → 寅
  8: 3,   // 壬 → 卯
  9: 6,   // 癸 → 午
};

/**
 * 현침살 (懸針殺) — 글자 모양이 바늘처럼 뾰족한 간지
 *
 * 해당 천간: 甲(0), 辛(7)
 */
export const HYEONCHIM_STEMS: readonly number[] = [0, 7];

/**
 * 현침살 (懸針殺) — 해당 지지
 *
 * 卯(3), 午(6), 未(7), 申(8)
 */
export const HYEONCHIM_BRANCHES: readonly number[] = [3, 6, 7, 8];

/**
 * 천의성 (天醫星) — 월지 기준
 *
 * 공식: `(월지 인덱스 - 1 + 12) % 12` (별도 테이블 불필요)
 */

/**
 * 금여록 (金輿祿) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const GEUMYEOROK: Record<number, number> = {
  0: 4,   // 甲 → 辰
  1: 5,   // 乙 → 巳
  2: 7,   // 丙 → 未
  3: 8,   // 丁 → 申
  4: 7,   // 戊 → 未
  5: 8,   // 己 → 申
  6: 10,  // 庚 → 戌
  7: 11,  // 辛 → 亥
  8: 1,   // 壬 → 丑
  9: 2,   // 癸 → 寅
};

/**
 * 백호살 (白虎殺) — 일주 간지 기준
 *
 * 육십갑자 인덱스: 甲辰(40), 乙未(31), 丙戌(22), 丁丑(13), 戊辰(4), 壬戌(58), 癸丑(49)
 */
export const BAEKHO_SAL_INDICES: readonly number[] = [4, 13, 22, 31, 40, 49, 58];

/**
 * 천라지망 (天羅地網)
 *
 * - 천라: 戌(10) + 亥(11) 쌍
 * - 지망: 辰(4) + 巳(5) 쌍
 */
export const CHEOLLA_BRANCHES: readonly number[] = [10, 11]; // 戌亥
export const JIMANG_BRANCHES: readonly number[] = [4, 5];    // 辰巳

/**
 * 학당귀인 (學堂貴人) — 일간 기준 (장생지)
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const HAKDANG_GUIIN: Record<number, number> = {
  0: 11,  // 甲 → 亥
  1: 6,   // 乙 → 午
  2: 2,   // 丙 → 寅
  3: 9,   // 丁 → 酉
  4: 2,   // 戊 → 寅
  5: 9,   // 己 → 酉
  6: 5,   // 庚 → 巳
  7: 0,   // 辛 → 子
  8: 8,   // 壬 → 申
  9: 3,   // 癸 → 卯
};

/**
 * 관귀학관 (官貴學館) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스 (쌍으로 묶임)
 */
export const GWANGUI_HAKGWAN: Record<number, number> = {
  0: 5,   // 甲 → 巳
  1: 5,   // 乙 → 巳
  2: 8,   // 丙 → 申
  3: 8,   // 丁 → 申
  4: 11,  // 戊 → 亥
  5: 11,  // 己 → 亥
  6: 2,   // 庚 → 寅
  7: 2,   // 辛 → 寅
  8: 2,   // 壬 → 寅
  9: 2,   // 癸 → 寅
};

/**
 * 천복귀인 (天福貴人) — 년지 기준, 지지 인덱스 → 지지 인덱스
 */
export const CHEONBOK_GUIIN: Record<number, number> = {
  0: 9,   // 子 → 酉
  1: 8,   // 丑 → 申
  2: 7,   // 寅 → 未
  3: 6,   // 卯 → 午
  4: 5,   // 辰 → 巳
  5: 4,   // 巳 → 辰
  6: 3,   // 午 → 卯
  7: 2,   // 未 → 寅
  8: 1,   // 申 → 丑
  9: 0,   // 酉 → 子
  10: 11, // 戌 → 亥
  11: 10, // 亥 → 戌
};

/**
 * 복성귀인 (福星貴人) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const BOKSEONG_GUIIN: Record<number, number> = {
  0: 2,   // 甲 → 寅
  1: 1,   // 乙 → 丑
  2: 0,   // 丙 → 子
  3: 11,  // 丁 → 亥
  4: 8,   // 戊 → 申
  5: 7,   // 己 → 未
  6: 6,   // 庚 → 午
  7: 5,   // 辛 → 巳
  8: 4,   // 壬 → 辰
  9: 3,   // 癸 → 卯
};

/**
 * 문곡귀인 (文曲貴人) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const MUNGOK_GUIIN: Record<number, number> = {
  0: 11,  // 甲 → 亥
  1: 0,   // 乙 → 子
  2: 2,   // 丙 → 寅
  3: 3,   // 丁 → 卯
  4: 2,   // 戊 → 寅
  5: 3,   // 己 → 卯
  6: 5,   // 庚 → 巳
  7: 6,   // 辛 → 午
  8: 8,   // 壬 → 申
  9: 9,   // 癸 → 酉
};

/**
 * 암록 (暗祿) — 일간 기준
 *
 * 건록지의 육합 위치. 일간 인덱스 → 지지 인덱스
 */
export const AMROK: Record<number, number> = {
  0: 11,  // 甲 → 亥
  1: 8,   // 乙 → 申
  2: 2,   // 丙 → 寅
  3: 11,  // 丁 → 亥
  4: 2,   // 戊 → 寅
  5: 11,  // 己 → 亥
  6: 5,   // 庚 → 巳
  7: 0,   // 辛 → 子
  8: 3,   // 壬 → 卯
  9: 6,   // 癸 → 午
};

/**
 * 재고귀인 (財庫貴人) — 일간 기준
 *
 * 오행별 묘고(墓庫) 위치. 일간 인덱스 → 지지 인덱스
 */
export const JAEGO_GUIIN: Record<number, number> = {
  0: 4,   // 甲 → 辰
  1: 4,   // 乙 → 辰
  2: 1,   // 丙 → 丑
  3: 1,   // 丁 → 丑
  4: 1,   // 戊 → 丑
  5: 1,   // 己 → 丑
  6: 7,   // 庚 → 未
  7: 7,   // 辛 → 未
  8: 10,  // 壬 → 戌
  9: 10,  // 癸 → 戌
};

/**
 * 비인살 (飛刃殺) — 일간 기준
 *
 * 양인의 충(沖) 위치. 일간 인덱스 → 지지 인덱스
 */
export const BIIN_SAL: Record<number, number> = {
  0: 9,   // 甲 → 酉
  1: 8,   // 乙 → 申
  2: 0,   // 丙 → 子
  3: 11,  // 丁 → 亥
  4: 0,   // 戊 → 子
  5: 11,  // 己 → 亥
  6: 3,   // 庚 → 卯
  7: 2,   // 辛 → 寅
  8: 6,   // 壬 → 午
  9: 5,   // 癸 → 巳
};

/**
 * 유하살 (流霞殺) — 일간 기준
 *
 * 일간 인덱스 → 지지 인덱스
 */
export const YUHA_SAL: Record<number, number> = {
  0: 9,   // 甲 → 酉
  1: 10,  // 乙 → 戌
  2: 7,   // 丙 → 未
  3: 8,   // 丁 → 申
  4: 5,   // 戊 → 巳
  5: 6,   // 己 → 午
  6: 4,   // 庚 → 辰
  7: 3,   // 辛 → 卯
  8: 11,  // 壬 → 亥
  9: 2,   // 癸 → 寅
};

/**
 * 급각살 (急脚殺) — 월지의 계절 그룹 기준
 *
 * 계절 그룹 → 매칭 지지 인덱스 배열
 */
export const GEUPGAK_SAL: Record<number, number[]> = {
  0: [11, 0],   // 寅卯辰 → 亥, 子
  1: [3, 7],    // 巳午未 → 卯, 未
  2: [2, 10],   // 申酉戌 → 寅, 戌
  3: [1, 4],    // 亥子丑 → 丑, 辰
};

/**
 * 원진살 (元辰殺) — 일지 기준
 *
 * 지지 인덱스 → 원진 지지 인덱스
 */
export const WONJIN_SAL: Record<number, number> = {
  0: 7,   // 子 → 未
  1: 6,   // 丑 → 午
  2: 5,   // 寅 → 巳
  3: 4,   // 卯 → 辰
  4: 3,   // 辰 → 卯
  5: 2,   // 巳 → 寅
  6: 1,   // 午 → 丑
  7: 0,   // 未 → 子
  8: 11,  // 申 → 亥
  9: 10,  // 酉 → 戌
  10: 9,  // 戌 → 酉
  11: 8,  // 亥 → 申
};

/**
 * 귀문관살 (鬼門關殺) — 쌍방향 지지 쌍
 *
 * 지지 인덱스 → 귀문 지지 인덱스
 */
export const GUIMUN_SAL: Record<number, number> = {
  0: 7,   // 子 → 未
  1: 6,   // 丑 → 午
  2: 9,   // 寅 → 酉
  3: 8,   // 卯 → 申
  4: 11,  // 辰 → 亥
  5: 10,  // 巳 → 戌
  6: 1,   // 午 → 丑
  7: 0,   // 未 → 子
  8: 3,   // 申 → 卯
  9: 2,   // 酉 → 寅
  10: 5,  // 戌 → 巳
  11: 4,  // 亥 → 辰
};

/**
 * 천희성 (天喜星) — 월지 기준
 *
 * 월지 인덱스 → 천희 지지 인덱스
 */
export const CHEONHUI_SEONG: Record<number, number> = {
  0: 9,   // 子月 → 酉
  1: 8,   // 丑月 → 申
  2: 7,   // 寅月 → 未
  3: 6,   // 卯月 → 午
  4: 5,   // 辰月 → 巳
  5: 4,   // 巳月 → 辰
  6: 3,   // 午月 → 卯
  7: 2,   // 未月 → 寅
  8: 1,   // 申月 → 丑
  9: 0,   // 酉月 → 子
  10: 11, // 戌月 → 亥
  11: 10, // 亥月 → 戌
};

/**
 * 황은대사 (皇恩大赦) — 월지 기준
 *
 * 일지·시지만 비교. 월지 인덱스 → 대상 지지 인덱스
 */
export const HWANGEUN_DAESA: Record<number, number> = {
  0: 8,   // 子月 → 申
  1: 7,   // 丑月 → 未
  2: 10,  // 寅月 → 戌
  3: 1,   // 卯月 → 丑
  4: 2,   // 辰月 → 寅
  5: 5,   // 巳月 → 巳
  6: 9,   // 午月 → 酉
  7: 3,   // 未月 → 卯
  8: 0,   // 申月 → 子
  9: 6,   // 酉月 → 午
  10: 11, // 戌月 → 亥
  11: 4,  // 亥月 → 辰
};

/**
 * 고신살 (孤神殺) — 년지의 계절 그룹 기준
 *
 * 계절 그룹 → 고신 지지 인덱스
 */
export const GOSIN_SAL: Record<number, number> = {
  0: 5,   // 寅卯辰 → 巳
  1: 8,   // 巳午未 → 申
  2: 11,  // 申酉戌 → 亥
  3: 2,   // 亥子丑 → 寅
};

/**
 * 과숙살 (寡宿殺) — 년지의 계절 그룹 기준
 *
 * 계절 그룹 → 과숙 지지 인덱스
 */
export const GWASUK_SAL: Record<number, number> = {
  0: 1,   // 寅卯辰 → 丑
  1: 4,   // 巳午未 → 辰
  2: 7,   // 申酉戌 → 未
  3: 10,  // 亥子丑 → 戌
};

/**
 * 상문살 (喪門殺) — 년지 기준
 *
 * 공식: `(년지 인덱스 + 2) % 12` (별도 테이블 불필요)
 */

/**
 * 음착살 (陰錯殺) — 일주 간지 기준
 *
 * 육십갑자 인덱스: 丁丑(13), 丁未(43), 辛卯(27), 辛酉(57), 癸巳(29), 癸亥(59)
 */
export const EUMCHAK_SAL_INDICES: readonly number[] = [13, 43, 27, 57, 29, 59];

/**
 * 양차살 (陽差殺) — 일주 간지 기준
 *
 * 양간 일주 6개: 丙子(12), 戊寅(14), 壬辰(28), 丙午(42), 戊申(44), 壬戌(58)
 */
export const YANGCHA_SAL_INDICES: readonly number[] = [12, 14, 28, 42, 44, 58];

/**
 * 탕화살 (湯火殺) — 일간 기준, 일간 인덱스 → 지지 인덱스
 */
export const TANGHWA_SAL: Record<number, number> = {
  0: 6,   // 甲 → 午
  1: 5,   // 乙 → 巳
  2: 8,   // 丙 → 申
  3: 7,   // 丁 → 未
  4: 8,   // 戊 → 申
  5: 7,   // 己 → 未
  6: 10,  // 庚 → 戌
  7: 9,   // 辛 → 酉
  8: 0,   // 壬 → 子
  9: 11,  // 癸 → 亥
};

// ═══════════════════════════════════════════════════════════════════
// sal04: 건록 (建祿) — 일간 기준
// ═══════════════════════════════════════════════════════════════════

/**
 * 건록 (建祿) — 일간 기준
 *
 * 일간 인덱스 → 건록 지지 인덱스
 */
export const GUNROK: Record<number, number> = {
  0: 2,   // 甲 → 寅
  1: 3,   // 乙 → 卯
  2: 5,   // 丙 → 巳
  3: 6,   // 丁 → 午
  4: 5,   // 戊 → 巳
  5: 6,   // 己 → 午
  6: 8,   // 庚 → 申
  7: 9,   // 辛 → 酉
  8: 11,  // 壬 → 亥
  9: 0,   // 癸 → 子
};

// ═══════════════════════════════════════════════════════════════════
// sal05: 일주 전용 신살 (오묘, 고란, 팔전)
// ═══════════════════════════════════════════════════════════════════

/**
 * 오묘살 (五墓) — 일간 기준, 일주 전용
 *
 * 일간 인덱스 → 오묘 지지 인덱스 (일지와 비교)
 */
export const OMYO_SAL: Record<number, number> = {
  0: 7,   // 甲 → 未
  1: 7,   // 乙 → 未
  2: 10,  // 丙 → 戌
  3: 10,  // 丁 → 戌
  4: 10,  // 戊 → 戌
  5: 1,   // 己 → 丑
  6: 1,   // 庚 → 丑
  7: 1,   // 辛 → 丑
  8: 4,   // 壬 → 辰
  9: 4,   // 癸 → 辰
};

/**
 * 고란살 (孤鸞殺) — 일주 간지 기준
 *
 * 육십갑자 인덱스:
 * 乙巳(41), 丙午(42), 戊申(44), 辛亥(47), 壬子(48), 甲寅(50), 丁巳(53)
 */
export const GORAN_INDICES: readonly number[] = [41, 42, 44, 47, 48, 50, 53];

/**
 * 팔전살 (八專殺) — 일주 간지 기준
 *
 * 丙午(42), 丁未(43), 壬子(48), 癸丑(49), 甲寅(50), 乙卯(51), 戊午(54), 己未(55), 庚申(56), 辛酉(57)
 */
export const PALJEON_INDICES: readonly number[] = [42, 43, 48, 49, 50, 51, 54, 55, 56, 57];

// ═══════════════════════════════════════════════════════════════════
// sal07: 월지 기준 지지 신살
// ═══════════════════════════════════════════════════════════════════

// 도화살(桃花) = 12신살 연살(年殺)과 동일 → TWELVE_SINSAL_TABLE[group][4]

/**
 * 홍란살 (紅鸞殺) — 년지 기준
 *
 * 년지 인덱스 → 홍란 지지 인덱스
 */
export const HONGRAN_SAL: Record<number, number> = {
  0: 3,   // 子 → 卯
  1: 2,   // 丑 → 寅
  2: 1,   // 寅 → 丑
  3: 0,   // 卯 → 子
  4: 11,  // 辰 → 亥
  5: 10,  // 巳 → 戌
  6: 9,   // 午 → 酉
  7: 8,   // 未 → 申
  8: 7,   // 申 → 未
  9: 6,   // 酉 → 午
  10: 5,  // 戌 → 巳
  11: 4,  // 亥 → 辰
};

// ═══════════════════════════════════════════════════════════════════
// sal09/sal10: 년지 기준 → 일지/시지 신살 (11종 공유 + 건각 1종)
// ═══════════════════════════════════════════════════════════════════

/**
 * 안맹살 (眼盲) — 삼회방합(계절) 그룹별 동일값
 *
 * 계절 그룹 → 안맹 지지 인덱스
 * 寅卯辰→丑, 巳午未→申, 申酉戌→未, 亥子丑→寅
 */
export const ANMAENG_SAL: Record<number, number> = {
  0: 1,   // 寅卯辰(봄) → 丑
  1: 8,   // 巳午未(여름) → 申
  2: 7,   // 申酉戌(가을) → 未
  3: 2,   // 亥子丑(겨울) → 寅
};

/**
 * 단명살 (短命) — 년지 기준
 *
 * 년지 인덱스 → 단명 지지 인덱스 (巳寅辰未 4값 3주기 반복)
 */
export const DANMYEONG_SAL: Record<number, number> = {
  0: 5,   // 子 → 巳
  1: 2,   // 丑 → 寅
  2: 4,   // 寅 → 辰
  3: 7,   // 卯 → 未
  4: 5,   // 辰 → 巳
  5: 2,   // 巳 → 寅
  6: 4,   // 午 → 辰
  7: 7,   // 未 → 未
  8: 5,   // 申 → 巳
  9: 2,   // 酉 → 寅
  10: 4,  // 戌 → 辰
  11: 7,  // 亥 → 未
};

/**
 * 직난살 (直難) — 년지 기준
 *
 * 년지 인덱스 → 대상 지지 인덱스 배열 (1~2개)
 */
export const JIKNAN_SAL: Record<number, number[]> = {
  0: [4],        // 子 → 辰
  1: [4],        // 丑 → 辰
  2: [6],        // 寅 → 午
  3: [6],        // 卯 → 午
  4: [7],        // 辰 → 未
  5: [7],        // 巳 → 未
  6: [3, 10],   // 午 → 卯, 戌
  7: [3, 10],   // 未 → 卯, 戌
  8: [5, 8],    // 申 → 巳, 申
  9: [5, 8],    // 酉 → 巳, 申
  10: [2, 3],   // 戌 → 寅, 卯
  11: [2, 3],   // 亥 → 寅, 卯
};

/**
 * 야제살 (夜諦) — 삼회방합(계절) 그룹별 삼합국 장성 위치
 *
 * 계절 그룹 → 야제 지지 인덱스
 */
export const YAJE_SAL: Record<number, number> = {
  0: 6,   // 寅卯辰(봄) → 午
  1: 9,   // 巳午未(여름) → 酉
  2: 0,   // 申酉戌(가을) → 子
  3: 3,   // 亥子丑(겨울) → 卯
};

/**
 * 무정살 (無情) — 삼회방합(계절) 그룹별 대상 지지
 *
 * 계절 그룹 → 대상 지지 인덱스 배열
 */
export const MUJEONG_SAL: Record<number, number[]> = {
  0: [0, 2, 9],    // 寅卯辰(봄) → 子, 寅, 酉
  1: [5, 10, 11],  // 巳午未(여름) → 巳, 戌, 亥
  2: [1, 8],       // 申酉戌(가을) → 丑, 申
  3: [0, 6],       // 亥子丑(겨울) → 子, 午
};

/**
 * 사계살 (四季) — 삼회방합(계절) 그룹별 대상 지지
 *
 * 계절 그룹 → 대상 지지 인덱스 배열
 */
export const SAGYE_SAL: Record<number, number[]> = {
  0: [5, 1],       // 寅卯辰(봄) → 巳, 丑
  1: [4, 8],       // 巳午未(여름) → 辰, 申
  2: [7, 11],      // 申酉戌(가을) → 未, 亥
  3: [2, 10],      // 亥子丑(겨울) → 寅, 戌
};

/**
 * 염왕살 (閻王) — 삼회방합(계절) 그룹별 대상 지지
 *
 * 계절 그룹 → 대상 지지 인덱스 배열
 */
export const YEOMWANG_SAL: Record<number, number[]> = {
  0: [1, 7],       // 寅卯辰(봄) → 丑, 未
  1: [4, 10],      // 巳午未(여름) → 辰, 戌
  2: [0, 6],       // 申酉戌(가을) → 子, 午
  3: [2, 3],       // 亥子丑(겨울) → 寅, 卯
};

/**
 * 건각살 (蹇脚) — sal09 전용 (년지 → 일지)
 *
 * 년지 인덱스 → 건각 대상 지지 인덱스
 */
export const GEONGAK_SAL: Record<number, number> = {
  0: 6,   // 子 → 午
  1: 0,   // 丑 → 子
  2: 1,   // 寅 → 丑
  3: 7,   // 卯 → 未
  4: 2,   // 辰 → 寅
  5: 8,   // 巳 → 申
  6: 3,   // 午 → 卯
  7: 9,   // 未 → 酉
  8: 4,   // 申 → 辰
  9: 10,  // 酉 → 戌
  10: 5,  // 戌 → 巳
  11: 11, // 亥 → 亥
};

// ═══════════════════════════════════════════════════════════════════
// sal11: 천소살, 격각살
// ═══════════════════════════════════════════════════════════════════

/**
 * 천소살 (天掃) — 천간 보합 쌍 + 지지 매핑
 *
 * 참조천간(년간) 인덱스 → { complementStemIdx: 대응천간 인덱스, branchIdx: 해당 지지 인덱스 }
 */
export const CHEONSO_SAL: Record<number, { complementStemIdx: number; branchIdx: number }> = {
  0: { complementStemIdx: 9, branchIdx: 7 },   // 甲 → 癸, 未
  1: { complementStemIdx: 8, branchIdx: 6 },   // 乙 → 壬, 午
  2: { complementStemIdx: 7, branchIdx: 5 },   // 丙 → 辛, 巳
  3: { complementStemIdx: 6, branchIdx: 4 },   // 丁 → 庚, 辰
  4: { complementStemIdx: 5, branchIdx: 3 },   // 戊 → 己, 卯
  5: { complementStemIdx: 4, branchIdx: 2 },   // 己 → 戊, 寅
  6: { complementStemIdx: 3, branchIdx: 1 },   // 庚 → 丁, 丑
  7: { complementStemIdx: 2, branchIdx: 0 },   // 辛 → 丙, 子
  8: { complementStemIdx: 1, branchIdx: 11 },  // 壬 → 乙, 亥
  9: { complementStemIdx: 0, branchIdx: 10 },  // 癸 → 甲, 戌
};

// ═══════════════════════════════════════════════════════════════════
// 삼재 (三災)
// ═══════════════════════════════════════════════════════════════════

/**
 * 삼재 (三災) — 삼합 그룹 기준
 *
 * 삼합 그룹 → [입삼재, 중삼재, 출삼재] 지지 인덱스 배열
 */
export const SAMJAE_SAL: Record<number, readonly number[]> = {
  0: [2, 3, 4],     // 申子辰 → 寅, 卯, 辰
  1: [8, 9, 10],    // 寅午戌 → 申, 酉, 戌
  2: [11, 0, 1],    // 巳酉丑 → 亥, 子, 丑
  3: [5, 6, 7],     // 亥卯未 → 巳, 午, 未
};

// ═══════════════════════════════════════════════════════════════════
// sal06/sal13/sal14/sal16 batch sinsal
// ═══════════════════════════════════════════════════════════════════

/** 육수살 (六秀) — 일주 간지 기준 */
export const YUKSU_INDICES: readonly number[] = [24, 25, 42, 43, 54, 55];
// 戊子(24), 己丑(25), 丙午(42), 丁未(43), 戊午(54), 己未(55)

/** 효신살 (梟神) — 일주 간지 기준 */
export const HYOSIN_INDICES: readonly number[] = [0, 2, 3, 5, 7, 8, 9, 11, 16, 37, 46, 54];
// 甲子(0), 丙寅(2), 丁卯(3), 己巳(5), 辛未(7), 壬申(8), 癸酉(9), 乙亥(11), 庚辰(16), 辛丑(37), 庚戌(46), 戊午(54)

/** 양진살 (陽進) — 일주 간지 기준 */
export const YANGJIN_INDICES: readonly number[] = [0, 30];
// 甲子(0), 甲午(30)

/** 음진살 (陰進) — 일주 간지 기준 */
export const EUMJIN_INDICES: readonly number[] = [15, 45];
// 己卯(15), 己酉(45)

/** 평두살 (平頭) — 일주 간지 기준 */
export const PYEONGDU_INDICES: readonly number[] = [0, 2, 22, 40, 50, 52];
// 甲子(0), 丙寅(2), 丙戌(22), 甲辰(40), 甲寅(50), 丙辰(52)

/** 금신살 (金神) — 일주 간지 기준 */
export const GEUMSIN_INDICES: readonly number[] = [1, 5, 9];
// 乙丑(1), 己巳(5), 癸酉(9)

/** 월합 (月合) — 월지→천간 인덱스 */
export const WOLHAP: Record<number, number> = {
  0: 3,   // 子 → 丁
  1: 1,   // 丑 → 乙
  2: 7,   // 寅 → 辛
  3: 5,   // 卯 → 己
  4: 3,   // 辰 → 丁
  5: 1,   // 巳 → 乙
  6: 7,   // 午 → 辛
  7: 5,   // 未 → 己
  8: 3,   // 申 → 丁
  9: 1,   // 酉 → 乙
  10: 7,  // 戌 → 辛
  11: 5,  // 亥 → 己
};

/** 천옥살 (天獄) — 월지→천간 인덱스 */
export const CHEONOK_SAL: Record<number, number> = {
  0: 0,   // 子 → 甲
  1: 1,   // 丑 → 乙
  2: 2,   // 寅 → 丙
  3: 3,   // 卯 → 丁
  4: 4,   // 辰 → 戊
  5: 5,   // 巳 → 己
  6: 6,   // 午 → 庚
  7: 7,   // 未 → 辛
  8: 8,   // 申 → 壬
  9: 9,   // 酉 → 癸
  10: 0,  // 戌 → 甲
  11: 1,  // 亥 → 乙
};

/** 대패살 (大敗) — 년지→지지 */
export const DAEPAE_SAL: Record<number, number> = {
  0: 5, 1: 8, 2: 11, 3: 11, 4: 5, 5: 5, 6: 11, 7: 2, 8: 8, 9: 8, 10: 2, 11: 2,
};

/** 팔패살 (八敗) — 년지→지지 */
export const PALPAE_SAL: Record<number, number> = {
  0: 7, 1: 10, 2: 1, 3: 1, 4: 7, 5: 7, 6: 1, 7: 4, 8: 4, 9: 4, 10: 10, 11: 10,
};

/** 낭적살 (狼籍) — 년지→지지 */
export const NANGJEOK_SAL: Record<number, number> = {
  0: 4, 1: 8, 2: 7, 3: 7, 4: 3, 5: 3, 6: 7, 7: 11, 8: 8, 9: 8, 10: 11, 11: 11,
};

/** 절방살 (絶房) — 년지→지지 */
export const JEOLBANG_SAL: Record<number, number> = {
  0: 0, 1: 3, 2: 8, 3: 0, 4: 3, 5: 8, 6: 0, 7: 3, 8: 8, 9: 0, 10: 3, 11: 8,
};

/** 천모살 (天耗) — 년지→지지 */
export const CHEONMO_SAL: Record<number, number> = {
  0: 8, 1: 10, 2: 0, 3: 2, 4: 4, 5: 6, 6: 8, 7: 10, 8: 0, 9: 2, 10: 4, 11: 6,
};

/** 타모살 (他耗) — 년지→지지 */
export const TAMO_SAL: Record<number, number> = {
  0: 5, 1: 7, 2: 9, 3: 11, 4: 1, 5: 3, 6: 5, 7: 7, 8: 9, 9: 11, 10: 1, 11: 3,
};

/** 농아살 (聾啞) — 년지→지지 */
export const NONGA_SAL: Record<number, number> = {
  0: 9, 1: 6, 2: 9, 3: 0, 4: 9, 5: 6, 6: 9, 7: 0, 8: 9, 9: 6, 10: 9, 11: 0,
};

/** 맹인살 (盲人) — 계절 그룹→지지 */
export const MAENGIN_SAL: Record<number, number> = {
  0: 9,  // 寅卯辰 → 酉
  1: 4,  // 巳午未 → 辰
  2: 7,  // 申酉戌 → 未
  3: 10, // 亥子丑 → 戌
};

/** 수화살 (水火) — 계절 그룹→지지 배열 */
export const SUHWA_SAL: Record<number, number[]> = {
  0: [7, 10],  // 寅卯辰 → 未, 戌
  1: [1, 4],   // 巳午未 → 丑, 辰
  2: [9],      // 申酉戌 → 酉
  3: [1],      // 亥子丑 → 丑
};

/** 심수살 (深水) — 계절 그룹→지지 배열 */
export const SIMSU_SAL: Record<number, number[]> = {
  0: [2, 8],   // 寅卯辰 → 寅, 申
  1: [7],      // 巳午未 → 未
  2: [9],      // 申酉戌 → 酉
  3: [1],      // 亥子丑 → 丑
};

/** 장군살 (將軍) — 계절 그룹→지지 배열 */
export const JANGGUN_SAL: Record<number, number[]> = {
  0: [4, 9, 10],   // 寅卯辰 → 辰, 酉, 戌
  1: [0, 3, 7],    // 巳午未 → 子, 卯, 未
  2: [1, 2, 6],    // 申酉戌 → 丑, 寅, 午
  3: [5, 8, 11],   // 亥子丑 → 巳, 申, 亥
};

/** 혈분살 (血盆) — 계절 그룹→지지 배열 */
export const HYEOLBUN_SAL: Record<number, number[]> = {
  0: [4, 10],  // 寅卯辰 → 辰, 戌
  1: [1, 7],   // 巳午未 → 丑, 未
  2: [10, 11], // 申酉戌 → 戌, 亥
  3: [1, 2],   // 亥子丑 → 丑, 寅
};

/** 천혁살 (天赫) — 월지 계절→해당 일주 sexagenary indices */
export const CHEONHYEOK_INDICES: Record<number, readonly number[]> = {
  0: [14],  // 寅卯辰 → 戊寅(14)
  1: [14],  // 巳午未 → 戊寅(14)
  2: [44],  // 申酉戌 → 戊申(44)
  3: [0],   // 亥子丑 → 甲子(0)
};

/**
 * 상충살 (相沖) — sal13, 년지→지지
 *
 * 표준 6충(+6)과 다른 별도 매핑. 子→酉, 午→酉 등.
 */
export const SANGCHUNG_SAL: Record<number, number> = {
  0: 9, 1: 10, 2: 11, 3: 0, 4: 1, 5: 2, 6: 9, 7: 10, 8: 11, 9: 0, 10: 1, 11: 2,
};

// ═══════════════════════════════════════════════════════════════════
// sal13: 성별/복합 조건 신살
// ═══════════════════════════════════════════════════════════════════

/** 골파살 (骨破) 남성 — 년지→지지 */
export const GOLPA_SAL_MALE: Record<number, number> = {
  0: 3, 1: 4, 2: 11, 3: 6, 4: 1, 5: 2, 6: 9, 7: 10, 8: 5, 9: 0, 10: 7, 11: 8,
};
/** 골파살 (骨破) 여성 — 년지→지지 */
export const GOLPA_SAL_FEMALE: Record<number, number> = {
  0: 7, 1: 5, 2: 4, 3: 2, 4: 7, 5: 5, 6: 4, 7: 2, 8: 7, 9: 5, 10: 4, 11: 2,
};

/** 철소살 (鐵掃) 남성 — 년지→지지 */
export const CHEOLSO_SAL_MALE: Record<number, number> = {
  0: 2, 1: 7, 2: 5, 3: 3, 4: 2, 5: 7, 6: 5, 7: 3, 8: 2, 9: 7, 10: 5, 11: 3,
};
/** 철소살 (鐵掃) 여성 — 년지→지지 */
export const CHEOLSO_SAL_FEMALE: Record<number, number> = {
  0: 1, 1: 10, 2: 8, 3: 9, 4: 1, 5: 10, 6: 8, 7: 9, 8: 1, 9: 10, 10: 8, 11: 9,
};

/** 재가살 (再嫁) 여성 전용 — 년지→지지 (+6 = 六沖) */
export const JAEGA_SAL: Record<number, number> = {
  0: 6, 1: 7, 2: 8, 3: 9, 4: 10, 5: 11, 6: 0, 7: 1, 8: 2, 9: 3, 10: 4, 11: 5,
};

/**
 * 결항살 (結項) — 삼합 그룹 + 일주 sexagenary 조건
 *
 * 삼합 그룹 → 해당 일주 sexagenary indices
 * 寅午戌→丙午(42),庚午(6)  巳酉丑→辛酉(57)  申子辰→壬子(48)  亥卯未→乙卯(51)
 */
export const GYEOLHANG_INDICES: Record<number, readonly number[]> = {
  0: [48],      // 申子辰 → 壬子
  1: [42, 6],   // 寅午戌 → 丙午, 庚午
  2: [57],      // 巳酉丑 → 辛酉
  3: [51],      // 亥卯未 → 乙卯
};

/**
 * 폭패살 (暴敗) — 년지 → 대상 지지 배열 (O(1) 매핑)
 *
 * 未·戌·亥 → 寅·卯·辰, 子·辰·巳 → 巳·午·未, 丑·申·酉 → 申·酉·戌, 寅·卯·午 → 亥·子·丑
 */
export const POKPAE_SAL: Record<number, readonly number[]> = {
  0: [5, 6, 7],     // 子 → 巳·午·未
  1: [8, 9, 10],    // 丑 → 申·酉·戌
  2: [11, 0, 1],    // 寅 → 亥·子·丑
  3: [11, 0, 1],    // 卯 → 亥·子·丑
  4: [5, 6, 7],     // 辰 → 巳·午·未
  5: [5, 6, 7],     // 巳 → 巳·午·未
  6: [11, 0, 1],    // 午 → 亥·子·丑
  7: [2, 3, 4],     // 未 → 寅·卯·辰
  8: [8, 9, 10],    // 申 → 申·酉·戌
  9: [8, 9, 10],    // 酉 → 申·酉·戌
  10: [2, 3, 4],    // 戌 → 寅·卯·辰
  11: [2, 3, 4],    // 亥 → 寅·卯·辰
};

// ═══════════════════════════════════════════════════════════════════
// 납음오행 (納音五行) — 60갑자 → 오행
// ═══════════════════════════════════════════════════════════════════

/**
 * 납음오행 매핑 (30쌍)
 *
 * 육십갑자를 2개씩 묶은 30쌍에 대한 오행 배정.
 * 인덱스 = Math.floor(sexagenaryIndex / 2)
 */
const NAPEUM_OHAENG: readonly Ohaeng[] = [
  '金', '火', '木', '土', '金',  // 甲子乙丑→海中金, 丙寅丁卯→爐中火, 戊辰己巳→大林木, 庚午辛未→路傍土, 壬申癸酉→劍鋒金
  '火', '水', '土', '金', '木',  // 甲戌乙亥→山頭火, 丙子丁丑→澗下水, 戊寅己卯→城頭土, 庚辰辛巳→白蠟金, 壬午癸未→楊柳木
  '水', '土', '火', '木', '水',  // 甲申乙酉→泉中水, 丙戌丁亥→屋上土, 戊子己丑→霹靂火, 庚寅辛卯→松柏木, 壬辰癸巳→長流水
  '金', '火', '木', '土', '金',  // 甲午乙未→沙中金, 丙申丁酉→山下火, 戊戌己亥→平地木, 庚子辛丑→壁上土, 壬寅癸卯→金箔金
  '火', '水', '土', '金', '木',  // 甲辰乙巳→覆燈火, 丙午丁未→天河水, 戊申己酉→大驛土, 庚戌辛亥→釵釧金, 壬子癸丑→桑柘木
  '水', '土', '火', '木', '水',  // 甲寅乙卯→大溪水, 丙辰丁巳→沙中土, 戊午己未→天上火, 庚申辛酉→石榴木, 壬戌癸亥→大海水
];

/**
 * 납음오행을 반환한다.
 *
 * @param sexagenaryIndex - 육십갑자 인덱스 (0~59)
 * @returns 납음오행 (木/火/土/金/水)
 */
export function getNapeumOhaeng(sexagenaryIndex: number): Ohaeng {
  return NAPEUM_OHAENG[Math.floor(sexagenaryIndex / 2)];
}

/** 납음오행 이름 (30쌍) */
const NAPEUM_NAMES: readonly string[] = [
  '해중금', '노중화', '대림목', '노방토', '검봉금',
  '산두화', '간하수', '성두토', '백랍금', '양류목',
  '천중수', '옥상토', '벽력화', '송백목', '장류수',
  '사중금', '산하화', '평지목', '벽상토', '금박금',
  '복등화', '천하수', '대역토', '채천금', '상자목',
  '대계수', '사중토', '천상화', '석류목', '대해수',
];

/**
 * 납음오행 이름을 반환한다.
 *
 * @param sexagenaryIndex - 육십갑자 인덱스 (0~59)
 * @returns 납음 이름 (해중금, 노중화, ...)
 */
export function getNapeumName(sexagenaryIndex: number): string {
  return NAPEUM_NAMES[Math.floor(sexagenaryIndex / 2)];
}

/**
 * 지소살 (地掃) — 납음오행→지지 배열 (sal16)
 */
export const JISO_SAL: Record<Ohaeng, readonly number[]> = {
  '金': [6, 7, 8],    // 午·未·申
  '木': [3, 4, 5],    // 卯·辰·巳
  '土': [3, 4, 5],    // 卯·辰·巳
  '水': [9, 10, 11],  // 酉·戌·亥
  '火': [0, 1, 2],    // 子·丑·寅
};

/**
 * 신음살 (呻吟) — 납음오행→지지 (sal16)
 */
export const SINEUM_SAL: Record<Ohaeng, number> = {
  '金': 11,  // 亥
  '木': 2,   // 寅
  '水': 5,   // 巳
  '火': 5,   // 巳
  '土': 8,   // 申
};

// ── 관살류 14종 ──────────────────────────────────

/** 수옥관살 (囚獄) — 년지→일지 */
export const SUOK_SAL: Record<number, number> = {
  0: 6, 1: 9, 2: 0, 3: 9, 4: 6, 5: 3, 6: 0, 7: 9, 8: 6, 9: 3, 10: 0, 11: 9,
};

/** 탄함관살 (呑陷) — 년지→일지 */
export const TANHAM_SAL: Record<number, number> = {
  0: 10, 1: 2, 2: 7, 3: 5, 4: 4, 5: 8, 6: 2, 7: 2, 8: 5, 9: 0, 10: 0, 11: 10,
};

/** 단교관살 (斷橋) — 년지→일지 */
export const DANGYO_SAL: Record<number, number> = {
  0: 11, 1: 0, 2: 2, 3: 3, 4: 8, 5: 1, 6: 10, 7: 9, 8: 4, 9: 5, 10: 6, 11: 7,
};

/** 삼구관살 (三坵) — 계절그룹→일지 */
export const SAMGU_SAL: Record<number, number> = {
  0: 1,   // 寅卯辰 → 丑
  1: 4,   // 巳午未 → 辰
  2: 7,   // 申酉戌 → 未
  3: 10,  // 亥子丑 → 戌
};

/** 낙정관살 (落井) — 일간→지지 */
export const NAKJEONG_SAL: Record<number, number> = {
  0: 5, 1: 0, 2: 8, 3: 10, 4: 3, 5: 5, 6: 0, 7: 8, 8: 10, 9: 3,
};

/** 철사관살 (鐵蛇) — 일간→지지 배열 (乙(1)은 해당 없음) */
export const CHEOLSA_SAL: Record<number, number[]> = {
  0: [4],       // 甲 → 辰
  2: [7, 8],    // 丙 → 未, 申
  3: [7, 8],    // 丁 → 未, 申
  4: [2],       // 戊 → 寅
  5: [2],       // 己 → 寅
  6: [10],      // 庚 → 戌
  7: [10],      // 辛 → 戌
  8: [1],       // 壬 → 丑
  9: [1],       // 癸 → 丑
};

/** 천조관살 (天弔) — 삼합그룹→일지 배열 */
export const CHEONJO_SAL: Record<number, number[]> = {
  0: [5, 6],    // 申子辰 → 巳, 午
  1: [4, 6],    // 寅午戌 → 辰, 午
  2: [0, 3],    // 巳酉丑 → 子, 卯
  3: [6, 8],    // 亥卯未 → 午, 申
};

/** 야체관살 (夜諦) — 계절그룹→일지 배열 */
export const YACHE_SAL: Record<number, number[]> = {
  0: [0, 2, 9],    // 寅卯辰 → 子, 寅, 酉
  1: [5, 10, 11],  // 巳午未 → 巳, 戌, 亥
  2: [1, 8],       // 申酉戌 → 丑, 申
  3: [0, 6],       // 亥子丑 → 子, 午
};

/** 계비관살 (鷄飛) — 일간→지지 배열 */
export const GYEBI_SAL: Record<number, number[]> = {
  0: [5, 9, 1],       // 甲 → 巳酉丑
  1: [0],             // 乙 → 子
  2: [0],             // 丙 → 子
  3: [0],             // 丁 → 子
  4: [0],             // 戊 → 子
  5: [5, 9, 1],       // 己 → 巳酉丑
  6: [11, 3, 7],      // 庚 → 亥卯未
  7: [11, 3, 7],      // 辛 → 亥卯未
  8: [2, 6, 10],      // 壬 → 寅午戌
  9: [2, 6, 10],      // 癸 → 寅午戌
};

/** 취명관살 (取命) — 일간→지지 배열 */
export const CHWIMYEONG_SAL: Record<number, number[]> = {
  0: [8, 0, 4],       // 甲 → 申子辰
  1: [8, 0, 4],       // 乙 → 申子辰
  2: [8, 0, 4],       // 丙 → 申子辰
  3: [8, 0, 4],       // 丁 → 申子辰
  4: [11, 3, 7],      // 戊 → 亥卯未
  5: [11, 3, 7],      // 己 → 亥卯未
  6: [11, 3, 7],      // 庚 → 亥卯未
  7: [2, 6, 10],      // 辛 → 寅午戌
  8: [2, 6, 10],      // 壬 → 寅午戌
  9: [2, 6, 10],      // 癸 → 寅午戌
};

/** 백일관살 — 삼위 분류 */
export const SAMWI_YEOK: readonly number[] = [2, 8, 5, 11];   // 寅申巳亥 (四驛)
export const SAMWI_JEONG: readonly number[] = [0, 6, 3, 9];   // 子午卯酉 (四正)
export const SAMWI_GO: readonly number[] = [4, 10, 1, 7];     // 辰戌丑未 (四庫)

/**
 * 계절 그룹 매핑 (지지 인덱스 → 계절 그룹)
 *
 * - 寅(2), 卯(3), 辰(4) → 0 (봄)
 * - 巳(5), 午(6), 未(7) → 1 (여름)
 * - 申(8), 酉(9), 戌(10) → 2 (가을)
 * - 亥(11), 子(0), 丑(1) → 3 (겨울)
 *
 * @param branchIdx - 지지 인덱스 (0~11)
 * @returns 계절 그룹 인덱스 (0~3)
 */
export function getSeasonGroup(branchIdx: number): number {
  if (branchIdx >= 2 && branchIdx <= 4) return 0;   // 寅卯辰
  if (branchIdx >= 5 && branchIdx <= 7) return 1;   // 巳午未
  if (branchIdx >= 8 && branchIdx <= 10) return 2;  // 申酉戌
  return 3;                                          // 亥子丑
}
